import React, { useState, useEffect, useCallback } from 'react';
import {
  FiRefreshCw, FiSave, FiEye, FiEyeOff, FiCheck, FiAlertCircle,
  FiZap, FiExternalLink, FiWifi, FiWifiOff, FiCopy, FiAlertTriangle,
} from 'react-icons/fi';
import {
  getAdminStripeConfig, updateAdminStripeConfig, testAdminStripeConfig,
} from '../../api/admin';
import type { AdminStripeConfig } from '../../api/admin';

/* ── Plans visibles dans la section Price IDs ─────────────────────────────── */
const PLANS = [
  { code: 'free',       label: 'Free' },
  { code: 'standard',   label: 'Standard' },
  { code: 'entreprise', label: 'Entreprise' },
  { code: 'business',   label: 'Business' },
];

/* ── Détection du mode depuis la clé ─────────────────────────────────────── */
function detectMode(key: string): 'live' | 'test' | null {
  if (key.startsWith('pk_live') || key.startsWith('sk_live')) return 'live';
  if (key.startsWith('pk_test') || key.startsWith('sk_test')) return 'test';
  return null;
}

/* ── Badge dernier test ───────────────────────────────────────────────────── */
const TestBadge: React.FC<{ status: 'ok' | 'error' | null; testedAt: string | null }> = ({ status, testedAt }) => {
  if (!status || !testedAt) return null;
  const date = new Date(testedAt).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' });
  return status === 'ok' ? (
    <span className="flex items-center gap-1 text-xs text-green-700 bg-green-50 border border-green-200 px-2 py-0.5 rounded-full">
      <FiWifi size={11} /> Dernier test OK — {date}
    </span>
  ) : (
    <span className="flex items-center gap-1 text-xs text-red-700 bg-red-50 border border-red-200 px-2 py-0.5 rounded-full">
      <FiWifiOff size={11} /> Dernier test échoué — {date}
    </span>
  );
};

/* ── Composant principal ──────────────────────────────────────────────────── */
const AdminStripeConfigPage: React.FC = () => {
  const [config,  setConfig]  = useState<AdminStripeConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [success, setSuccess] = useState(false);
  const [error,   setError]   = useState<string | null>(null);

  // Formulaire clés
  const [publishableKey, setPublishableKey] = useState('');
  const [secretKey,      setSecretKey]      = useState('');
  const [webhookSecret,  setWebhookSecret]  = useState('');
  const [isActive,       setIsActive]       = useState(false);
  const [show, setShow] = useState({ secret: false, webhook: false });

  // Price IDs par plan
  const [priceIds, setPriceIds] = useState<Record<string, { monthly: string; yearly: string }>>({});

  // Test connexion
  const [testing,    setTesting]    = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);

  // Copy webhook URL
  const [copied, setCopied] = useState(false);

  const webhookUrl = `${window.location.origin.replace(':3000', ':5000')}/api/payments/webhooks/stripe`;

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const { config: data } = await getAdminStripeConfig();
      if (data) {
        setConfig(data);
        setPublishableKey(data.publishable_key || '');
        setIsActive(data.is_active);
        setPriceIds(data.price_ids || {});
      }
    } catch { setError('Impossible de charger la configuration Stripe'); }
    finally  { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true); setError(null); setSuccess(false);
    try {
      const payload: any = { publishable_key: publishableKey, is_active: isActive, price_ids: priceIds };
      if (secretKey.trim())     payload.secret_key     = secretKey.trim();
      if (webhookSecret.trim()) payload.webhook_secret = webhookSecret.trim();
      const { config: updated } = await updateAdminStripeConfig(payload);
      setConfig(updated);
      setSecretKey(''); setWebhookSecret('');
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch { setError('Échec de la sauvegarde'); }
    finally  { setSaving(false); }
  };

  const runTest = async () => {
    setTesting(true); setTestResult(null);
    try {
      const result = await testAdminStripeConfig();
      setTestResult(result);
      if (result.success) {
        setConfig(c => c ? { ...c, test_status: 'ok', last_tested_at: new Date().toISOString() } : c);
      } else {
        setConfig(c => c ? { ...c, test_status: 'error', last_tested_at: new Date().toISOString() } : c);
      }
    } catch { setTestResult({ success: false, message: 'Erreur réseau' }); }
    finally  { setTesting(false); }
  };

  const copyWebhook = () => {
    navigator.clipboard.writeText(webhookUrl).then(() => {
      setCopied(true); setTimeout(() => setCopied(false), 2000);
    });
  };

  // Détection mode depuis les clés saisies
  const pkMode  = detectMode(publishableKey);
  const skMode  = detectMode(secretKey);
  const modeMismatch = pkMode && skMode && pkMode !== skMode;

  const isConfigured = config?.has_secret_key && config?.has_webhook_secret && !!config?.publishable_key;

  return (
    <div className="p-6 max-w-5xl mx-auto">

      {/* ── Header ── */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <FiZap className="text-indigo-600" /> Configuration Stripe
          </h2>
          <p className="text-sm text-gray-500 mt-0.5">Clés API, webhook et Price IDs — paiements et abonnements</p>
        </div>
        <div className="flex gap-2">
          {config?.has_secret_key && (
            <button onClick={runTest} disabled={testing}
              className="flex items-center gap-2 px-3 py-1.5 border border-indigo-300 text-indigo-700 rounded-lg text-sm hover:bg-indigo-50 transition disabled:opacity-50">
              {testing
                ? <FiRefreshCw size={14} className="animate-spin" />
                : <FiWifi size={14} />}
              Tester la connexion
            </button>
          )}
          <button onClick={load}
            className="flex items-center gap-2 px-3 py-1.5 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition">
            <FiRefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Actualiser
          </button>
        </div>
      </div>

      {/* ── Statut global ── */}
      {!loading && config && (
        <div className={`mb-5 p-3 rounded-xl border flex items-center gap-3 ${
          isConfigured ? 'bg-green-50 border-green-200' : 'bg-orange-50 border-orange-200'
        }`}>
          {isConfigured ? (
            <>
              <FiCheck className="text-green-600 flex-shrink-0" size={18} />
              <div className="flex-1 flex items-center gap-3 flex-wrap">
                <p className="text-sm font-medium text-green-800">
                  Stripe configuré —{' '}
                  <span className={`font-semibold ${config.mode === 'live' ? 'text-green-700' : 'text-yellow-700'}`}>
                    {config.mode === 'live' ? '🟢 MODE PRODUCTION (LIVE)' : '🟡 MODE TEST'}
                  </span>
                </p>
                <TestBadge status={config.test_status} testedAt={config.last_tested_at} />
              </div>
              <a href="https://dashboard.stripe.com" target="_blank" rel="noreferrer"
                className="flex items-center gap-1 text-xs text-indigo-600 hover:underline flex-shrink-0">
                Dashboard <FiExternalLink size={11} />
              </a>
            </>
          ) : (
            <>
              <FiAlertCircle className="text-orange-600 flex-shrink-0" size={18} />
              <p className="text-sm font-medium text-orange-800">
                Configuration incomplète — les paiements ne fonctionneront pas
              </p>
            </>
          )}
        </div>
      )}

      {/* ── Résultat test ── */}
      {testResult && (
        <div className={`mb-4 p-3 rounded-xl border flex items-center gap-2 text-sm ${
          testResult.success
            ? 'bg-green-50 border-green-200 text-green-800'
            : 'bg-red-50 border-red-200 text-red-800'
        }`}>
          {testResult.success ? <FiWifi size={15} /> : <FiWifiOff size={15} />}
          {testResult.message}
        </div>
      )}

      {/* ── Mismatch warning ── */}
      {modeMismatch && (
        <div className="mb-4 p-3 rounded-xl border border-yellow-300 bg-yellow-50 flex items-center gap-2 text-sm text-yellow-800">
          <FiAlertTriangle size={15} /> Attention : la clé publiable est en mode <strong>{pkMode?.toUpperCase()}</strong> mais la clé secrète est en mode <strong>{skMode?.toUpperCase()}</strong> — incohérence détectée.
        </div>
      )}

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center gap-2">
          <FiAlertCircle size={14} /> {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12 text-gray-400">Chargement…</div>
      ) : (
        <div className="space-y-5">

          {/* ── Clés API ── */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-5 py-3 border-b border-gray-100 bg-gray-50">
              <h3 className="font-semibold text-gray-900 text-sm">Clés API Stripe</h3>
            </div>
            <div className="px-5 py-4 space-y-4">

              {/* Publishable key */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Clé publiable (Publishable Key)
                  {pkMode && (
                    <span className={`ml-2 text-xs font-medium px-1.5 py-0.5 rounded ${
                      pkMode === 'live' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                    }`}>
                      {pkMode === 'live' ? '🟢 LIVE' : '🟡 TEST'}
                    </span>
                  )}
                </label>
                <input
                  type="text"
                  value={publishableKey}
                  onChange={e => setPublishableKey(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 font-mono"
                  placeholder="pk_live_xxx ou pk_test_xxx"
                />
              </div>

              {/* Secret key */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Clé secrète (Secret Key)
                  {config?.has_secret_key && (
                    <span className="ml-2 text-green-600 font-normal text-xs">
                      <FiCheck size={11} className="inline" /> déjà configurée — laisser vide pour conserver
                    </span>
                  )}
                </label>
                <div className="relative">
                  <input
                    type={show.secret ? 'text' : 'password'}
                    value={secretKey}
                    onChange={e => setSecretKey(e.target.value)}
                    className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 font-mono"
                    placeholder={config?.has_secret_key ? '••••••••••••••••' : 'sk_live_xxx ou sk_test_xxx'}
                  />
                  <button type="button" onClick={() => setShow(s => ({ ...s, secret: !s.secret }))}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                    {show.secret ? <FiEyeOff size={16} /> : <FiEye size={16} />}
                  </button>
                </div>
              </div>

              {/* Webhook secret */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Webhook Secret
                  {config?.has_webhook_secret && (
                    <span className="ml-2 text-green-600 font-normal text-xs">
                      <FiCheck size={11} className="inline" /> déjà configuré — laisser vide pour conserver
                    </span>
                  )}
                </label>
                <div className="relative">
                  <input
                    type={show.webhook ? 'text' : 'password'}
                    value={webhookSecret}
                    onChange={e => setWebhookSecret(e.target.value)}
                    className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 font-mono"
                    placeholder={config?.has_webhook_secret ? '••••••••••••••••' : 'whsec_xxx'}
                  />
                  <button type="button" onClick={() => setShow(s => ({ ...s, webhook: !s.webhook }))}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                    {show.webhook ? <FiEyeOff size={16} /> : <FiEye size={16} />}
                  </button>
                </div>
              </div>

              {/* Active toggle */}
              <div className="flex items-center gap-2">
                <input type="checkbox" id="stripe-active" checked={isActive} onChange={e => setIsActive(e.target.checked)}
                  className="h-4 w-4 text-indigo-600 border-gray-300 rounded" />
                <label htmlFor="stripe-active" className="text-sm text-gray-700">Activer les paiements Stripe</label>
              </div>
            </div>
          </div>

          {/* ── URL Webhook ── */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-5 py-3 border-b border-gray-100 bg-gray-50">
              <h3 className="font-semibold text-gray-900 text-sm">URL Webhook Stripe</h3>
            </div>
            <div className="px-5 py-4">
              <p className="text-xs text-gray-500 mb-2">
                À configurer dans le{' '}
                <a href="https://dashboard.stripe.com/webhooks" target="_blank" rel="noreferrer"
                  className="text-indigo-600 hover:underline inline-flex items-center gap-0.5">
                  Dashboard Stripe → Webhooks <FiExternalLink size={11} />
                </a>
              </p>
              <div className="flex items-center gap-2">
                <code className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm font-mono text-gray-700 truncate">
                  {webhookUrl}
                </code>
                <button onClick={copyWebhook}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm border transition ${
                    copied
                      ? 'bg-green-50 border-green-200 text-green-700'
                      : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}>
                  {copied ? <FiCheck size={14} /> : <FiCopy size={14} />}
                  {copied ? 'Copié' : 'Copier'}
                </button>
              </div>
              <p className="text-xs text-gray-400 mt-2">
                Événements à écouter : <code className="bg-gray-100 px-1 rounded">customer.subscription.*</code>{' '}
                <code className="bg-gray-100 px-1 rounded">invoice.*</code>{' '}
                <code className="bg-gray-100 px-1 rounded">payment_intent.*</code>
              </p>
            </div>
          </div>

          {/* ── Price IDs par plan ── */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-5 py-3 border-b border-gray-100 bg-gray-50 flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-gray-900 text-sm">Price IDs Stripe par plan</h3>
                <p className="text-xs text-gray-500 mt-0.5">Tableau de bord Stripe → Produits → votre produit → ID du prix</p>
              </div>
            </div>
            <div className="px-5 py-4">
              <div className="grid grid-cols-1 gap-4">
                {PLANS.map(plan => (
                  <div key={plan.code} className="border border-gray-100 rounded-lg p-3">
                    <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">{plan.label}</p>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Mensuel</label>
                        <input
                          type="text"
                          value={priceIds[plan.code]?.monthly || ''}
                          onChange={e => setPriceIds(prev => ({
                            ...prev,
                            [plan.code]: { ...prev[plan.code], monthly: e.target.value },
                          }))}
                          className="w-full px-2.5 py-1.5 border border-gray-300 rounded-md text-xs font-mono focus:outline-none focus:ring-2 focus:ring-indigo-400"
                          placeholder="price_xxx"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Annuel</label>
                        <input
                          type="text"
                          value={priceIds[plan.code]?.yearly || ''}
                          onChange={e => setPriceIds(prev => ({
                            ...prev,
                            [plan.code]: { ...prev[plan.code], yearly: e.target.value },
                          }))}
                          className="w-full px-2.5 py-1.5 border border-gray-300 rounded-md text-xs font-mono focus:outline-none focus:ring-2 focus:ring-indigo-400"
                          placeholder="price_xxx"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* ── Bouton Sauvegarder ── */}
          <div className="flex items-center justify-between pt-1">
            {success
              ? <span className="text-sm text-green-600 flex items-center gap-1"><FiCheck size={14} /> Sauvegardé</span>
              : <span />
            }
            <button onClick={save} disabled={saving}
              className="flex items-center gap-2 px-5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm rounded-lg transition disabled:opacity-50">
              {saving ? <FiRefreshCw size={14} className="animate-spin" /> : <FiSave size={14} />}
              Enregistrer
            </button>
          </div>

        </div>
      )}
    </div>
  );
};

export default AdminStripeConfigPage;
